import { useCallback, useEffect, useRef } from "react"

import { useProjectDescriptionTriage } from "@/api/useTriage"
import { App } from "@/atoms"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { TRIAGE_DRAWER_ID } from "@/organisms/Drawers/const"
import { useCounts } from "@/organisms/Drawers/hooks/useCounts"
import { useDrawer } from "@/organisms/Drawers/hooks/useDrawer"
import { useParamsFilter } from "@/organisms/Drawers/hooks/useParamsFilter"
import {
  useQuickWinIgnore,
  useQuickWinsData
} from "@/pages/TriagePage/application/useQuickWins"
import {
  BASE_IMAGE_NAME_KEY,
  BOTH_TYPES,
  buildInitialState,
  PACKAGE_NAME_KEY,
  TYPE_FILTER_KEY
} from "../../QuickWinsDrawer.constants"
import { QuickWinsFilterBar } from "../QuickWinsFilterBar"
import { QuickWinsList } from "../QuickWinsList"

import type { QuickWinType } from "@/domain/issue"
import type { TabComponentProps } from "@/organisms/Drawers/components/TabsDrawerTemplate"
import type { QuickWinsTab } from "@/pages/TriagePage/application/useQuickWins.types"
import type { QuickWinsDrawerMeta } from "../../QuickWinsDrawer.types"

interface QuickWinsTabContentProps extends TabComponentProps {
  tab: QuickWinsTab
}

export const QuickWinsTabContent = ({
  tab,
  isActive,
  record,
  ...restProps
}: QuickWinsTabContentProps) => {
  const { actions } = restProps
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { logger, EVENTS } = useLogger()
  const triageDrawer = useDrawer(TRIAGE_DRAWER_ID)
  const { ignore } = useQuickWinIgnore()

  const projectId = record?.id
  const meta = (
    record as { id?: string; meta?: QuickWinsDrawerMeta } | undefined
  )?.meta
  const metaKey = `${meta?.typeFilter || ""}-${meta?.searchQuery || ""}`

  // Filter state via useParamsFilter — matches IssuesTab pattern.
  // restProps includes drawerId, tabs, actions (with encode/decode) from TabsDrawerTemplate.
  const filtersContext = useParamsFilter({
    filterKey: tab,
    initialParams: buildInitialState(meta),
    ...restProps
  })

  const filterProviderKeyRef = useRef(0)
  const metaInitializedRef = useRef(false)

  // Reset filters when meta changes (e.g. opening from banner with different params).
  // Skip initial mount — useParamsFilter already initializes from URL tabs or initialParams.
  useEffect(() => {
    if (!metaInitializedRef.current) {
      metaInitializedRef.current = true
      return
    }
    filtersContext.updateParams(buildInitialState(meta))
    filterProviderKeyRef.current += 1
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metaKey])

  const prevParamsRef = useRef(filtersContext.params)

  // Log analytics when filter params change
  useEffect(() => {
    const prev = prevParamsRef.current
    const current = filtersContext.params

    Object.entries(current).forEach(([key, value]) => {
      if (value !== prev[key]) {
        logger.info(EVENTS.ANALYTIC_EVENTS.QUICK_WINS.DRAWER.FILTER_CHANGE, {
          projectId,
          filterName: key,
          filterValue: value
        })
      }
    })

    prevParamsRef.current = current
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersContext.params])

  const params = filtersContext.params as Record<string, string>
  const rawTypeFilter = params[TYPE_FILTER_KEY]
  const appliedTypeFilter = (
    rawTypeFilter && rawTypeFilter !== BOTH_TYPES ? rawTypeFilter : undefined
  ) as QuickWinType | undefined
  const appliedSearch =
    params[PACKAGE_NAME_KEY] || params[BASE_IMAGE_NAME_KEY] || undefined

  const hasFilters = Object.entries(params).some(
    ([key, value]) =>
      value && !(key === TYPE_FILTER_KEY && value === BOTH_TYPES)
  )

  const clearFilters = useCallback(() => {
    logger.info(EVENTS.ANALYTIC_EVENTS.QUICK_WINS.DRAWER.FILTER_CLEAR, {
      projectId
    })
    filtersContext.updateParams({ [TYPE_FILTER_KEY]: "" })
    filterProviderKeyRef.current += 1
  }, [logger, EVENTS, projectId, filtersContext])

  const projectDescriptionContext = useProjectDescriptionTriage({
    needle: projectId || ""
  })
  const projectName =
    projectDescriptionContext.response?.data?.resources?.[0]?.name

  const { response, isLoading } = useQuickWinsData(projectId, {
    tab,
    typeFilter: appliedTypeFilter,
    searchQuery: appliedSearch,
    issueId: meta?.filterByIssueId
  })

  const quickWins = response?.data || []

  // Report count to TabsDrawerTemplate for tab badge (uses shared useCounts pattern).
  useCounts(tab, { response }, { actions })

  // Track QW-specific tab change (skip initial render — only user-initiated switches)
  const initialTabRef = useRef(true)
  useEffect(() => {
    if (!isActive) return
    if (initialTabRef.current) {
      initialTabRef.current = false
      return
    }
    logger.info(EVENTS.ANALYTIC_EVENTS.QUICK_WINS.DRAWER.TAB_CHANGE, {
      projectId,
      tab
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive])

  const handleIgnore = useCallback(
    (quickWinId: string, shouldIgnore: boolean) => {
      ignore(quickWinId, shouldIgnore, {
        onSuccess: () => {
          message.info(
            shouldIgnore
              ? t("quickWins.movedToIgnore")
              : t("quickWins.movedToSuggested")
          )
        },
        onError: () => {
          message.error(t("layout.error.unknown"))
        }
      })
    },
    [ignore, message, t]
  )

  const handleIssueClick = useCallback(
    (issueKey: string) => {
      triageDrawer.onRowClick({ key: issueKey })
    },
    [triageDrawer]
  )

  return (
    <>
      {isActive && (
        <QuickWinsFilterBar
          record={record}
          onChange={filtersContext.onChange}
          filterProviderKey={filterProviderKeyRef.current}
          smartFilters={params}
        />
      )}
      <QuickWinsList
        quickWins={quickWins}
        isLoading={isLoading}
        onIgnore={handleIgnore}
        onIssueClick={handleIssueClick}
        tab={tab}
        hasFilters={hasFilters}
        onClearFilters={clearFilters}
        projectName={projectName}
      />
    </>
  )
}
